import { AUTHORS } from '../articles/authors';

import Box from '@mui/material/Box/Box';
import HeaderSectionImage from 'assets/resources/classical-simulation-testing-doesnt-scale/main.webp';

import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';
import { Button } from 'components/Buttons';

const commonData = {
  title: "Classical Simulation Testing Doesn't Scale",
  publishDate: 'March 2, 2026',
  minutesOfRead: 10,
  ...AUTHORS.AILiveSim,
};

export const classicalSimulationTestingMeta: PageMeta = {
  url: '/technical-briefs/classical-simulation-testing-doesnt-scale',
  title: commonData.title,
  description:
    'Introducing Intelligent System Testing (IST) — AILiveSim\'s adaptive approach that focuses simulations where failures are most likely. See how 800 targeted simulations reveal precise failure boundaries in an autonomous docking use case.',
};

export const classicalSimulationTestingData: ArticleSections[] = [
  {
    ...commonData,
    image: HeaderSectionImage,
    text: (
      <>
        As systems grow more complex, exhaustive parameter scanning quickly becomes inefficient,
        expensive, and blind to critical edge cases. In this brief, we introduce Intelligent System
        Testing (IST) — AILiveSim&apos;s adaptive testing approach that focuses simulations where
        failures are most likely to occur. Instead of running more tests, IST runs the right tests.
      </>
    ),
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    resourceType: 'Technical Brief',
  },
  {
    type: 'common',
    title: 'The Challenge',
    text: (
      <>
        <p>
          Through a real autonomous docking use case, we show how 800 targeted simulations, executed
          in just hours, reveal precise failure boundaries, high-risk regimes, and interpretable
          validation insights.
        </p>

        <p>
          If you&apos;re working on autonomy, validation, or safety-critical systems, this changes
          how you think about simulation campaigns.
        </p>
      </>
    ),
  },
  {
    type: 'common',
    title: 'Download Full Technical Brief',
    text: (
      <>
        <p>... to see how Intelligent System Testing works in practice.</p>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginTop: '24px',
          }}
        >
          <Button
            btnType="secondary"
            style={{ color: '#348EE9', borderColor: '#348EE9', height: '40px' }}
            onClick={() => {
              const win = window as typeof window & {
                openResourceAccessModal?: (config: {
                  url: string;
                  title: string;
                  pageTag: string;
                }) => void;
              };
              if (win.openResourceAccessModal) {
                win.openResourceAccessModal({
                  url: 'https://www.ailivesim.com/downloadableResources/Technical_Insight_Adaptive_Simulation_Based_Validation_for_Autonomous_Systems.pdf',
                  title: commonData.title,
                  pageTag: 'technical-brief-classical-simulation-testing-doesnt-scale-download',
                });
              }
            }}
          >
            Download Technical Brief
          </Button>
        </Box>
      </>
    ),
  },
];

export const classicalSimulationTestingCard: TCardProps = {
  description:
    'Introducing Intelligent System Testing (IST) — AILiveSim\'s adaptive approach that focuses simulations where failures are most likely. 800 targeted simulations reveal precise failure boundaries in autonomous docking.',
  image: HeaderSectionImage,
  type: 'Technical Brief',
  category: 'technical-briefs',
  postUrl: classicalSimulationTestingMeta.url,
  ...commonData,
  hashTags: [EHashtag.AISimulation, EHashtag.SafetyValidation, EHashtag.SyntheticData],
};
